import {t, useLanguage} from '../i18n';
import {interfaceLocale} from '../i18n';
import {usePendingEditor} from "../hooks/useEditorNavigation";
import {validateBusinessHours} from "../utils/businessEditorValidation";
// src/sections/AvailabilitySection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { loopBusinessNow, loopBusinessUpdateDoc } from "../utils/businessNativeSafeWrites";
import { db as defaultDb } from "../firebase";

const DAYS = [
  { key: "monday", label: "Lunes", short: "Lun" },
  { key: "tuesday", label: "Martes", short: "Mar" },
  { key: "wednesday", label: "Miércoles", short: "Mié" },
  { key: "thursday", label: "Jueves", short: "Jue" },
  { key: "friday", label: "Viernes", short: "Vie" },
  { key: "saturday", label: "Sábado", short: "Sáb" },
  { key: "sunday", label: "Domingo", short: "Dom" },
];

const DEFAULT_AVAILABILITY = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
};

const NOTICE_OPTIONS = [
  { value: 0, label: "Mismo momento" },
  { value: 2, label: "2 horas antes" },
  { value: 6, label: "6 horas antes" },
  { value: 12, label: "12 horas antes" },
  { value: 24, label: "1 día antes" },
  { value: 48, label: "2 días antes" },
  { value: 72, label: "3 días antes" },
];

function normalizeRanges(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") {
    if (value.closed === true || value.enabled === false) return [];
    if (Array.isArray(value.ranges)) return value.ranges;
    if (value.from || value.start || value.fromTime || value.startTime) return [value];
  }
  return [];
}

function normalizeAvailability(raw) {
  const base = { ...DEFAULT_AVAILABILITY };
  const source = raw && typeof raw === "object" ? raw : {};
  DAYS.forEach((day) => {
    base[day.key] = normalizeRanges(source[day.key]).map((r) => ({
      from: r.from || r.start || r.fromTime || r.startTime || "09:00",
      to: r.to || r.end || r.toTime || r.endTime || "18:00",
    }));
  });
  return base;
}

function normalizeBizType(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function isEventLikeBusiness(businessDoc) {
  const fields = [
    businessDoc?.type,
    businessDoc?.category,
    businessDoc?.businessType,
    businessDoc?.vertical,
    businessDoc?.serviceCategory,
  ].map(normalizeBizType).filter(Boolean);

  return fields.some((x) =>
    x.includes("evento") ||
    x.includes("event") ||
    x.includes("fotografia") ||
    x.includes("animacion")
  );
}

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isPastBlockedRange(item) {
  const today = todayYmd();
  const to = String(item?.to || item?.from || "");
  return Boolean(to && to < today);
}

function normalizeBlockedRanges(data) {
  const ranges = [];

  if (Array.isArray(data?.blockedRanges)) {
    data.blockedRanges.forEach((r, idx) => {
      const from = String(r?.from || r?.date || "").trim();
      const to = String(r?.to || r?.from || r?.date || "").trim();
      if (!from) return;
      ranges.push({
        id: r?.id || `blocked-${idx}-${from}`,
        from,
        to: to || from,
        reason: String(r?.reason || r?.title || "No disponible").trim(),
      });
    });
  }

  if (!ranges.length && Array.isArray(data?.blockedDates)) {
    data.blockedDates.forEach((d, idx) => {
      const date = String(d || "").trim();
      if (!date) return;
      ranges.push({
        id: `blocked-date-${idx}-${date}`,
        from: date,
        to: date,
        reason: "No disponible",
      });
    });
  }

  return ranges;
}

function formatYmdNice(ymd) {
  if (!ymd) return "";
  try {
    const [y, m, d] = String(ymd).split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    return date.toLocaleDateString(interfaceLocale(), { day: "numeric", month: "short" });
  } catch (_) {
    return ymd;
  }
}

function formatRangeNice(item) {
  if (!item?.from) return "";
  if (!item.to || item.to === item.from) return formatYmdNice(item.from);
  return `${formatYmdNice(item.from)} – ${formatYmdNice(item.to)}`;
}

function Field({ label, children, hint }) {
  return (
    <label className="availability-field">
      <span>{label}</span>
      {children}
      {hint ? <em>{hint}</em> : null}
    </label>
  );
}

function MiniStepper({ value, onChange, min = 0, max = 99, suffix }) {
  const safe = Number(value || 0);
  const set = (next) => onChange(Math.max(min, Math.min(max, Number(next || 0))));

  return (
    <div className="availability-stepper">
      <button type="button" onClick={() => set(safe - 1)} disabled={safe <= min}>−</button>
      <div>
        <strong>{safe}</strong>
        {suffix ? <span>{suffix}</span> : null}
      </div>
      <button type="button" onClick={() => set(safe + 1)} disabled={safe >= max}>+</button>
    </div>
  );
}

function ConfigCard({ badge, title, copy, children }) {
  return (
    <article className="availability-config-card">
      <div className="availability-config-copy">
        <span>{t(badge)}</span>
        <h5>{title}</h5>
        <p>{copy}</p>
      </div>
      <div className="availability-config-control">{children}</div>
    </article>
  );
}

export default function AvailabilitySection({ businessId, businessDoc, db = defaultDb, externalSaveRef = null }) {
  const language = useLanguage();
  const [availability, setAvailability] = useState(() => normalizeAvailability(businessDoc?.availability));
  const [capacity, setCapacity] = useState(Number(businessDoc?.eventMaxSimultaneousBookings || businessDoc?.maxSimultaneousEvents || 3));
  const [bufferBefore, setBufferBefore] = useState(Number(businessDoc?.eventBufferBeforeMinutes || businessDoc?.bufferBeforeMinutes || 0));
  const [bufferAfter, setBufferAfter] = useState(Number(businessDoc?.eventBufferAfterMinutes || businessDoc?.bufferAfterMinutes || 0));
  const [noticeHours, setNoticeHours] = useState(Number(businessDoc?.minimumBookingNoticeHours || 0));
  const [blockedRanges, setBlockedRanges] = useState(() => normalizeBlockedRanges(businessDoc || {}));

  const [blockedFrom, setBlockedFrom] = useState("");
  const [blockedTo, setBlockedTo] = useState("");
  const [blockedReason, setBlockedReason] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [blockEditorOpen, setBlockEditorOpen] = useState(false);

  const [openDay, setOpenDay] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const loadedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const [savedSignature,setSavedSignature]=useState(null);
  const signature=JSON.stringify([availability,capacity,bufferBefore,bufferAfter,noticeHours,blockedRanges]);
  const pending=savedSignature!==null&&signature!==savedSignature;
  usePendingEditor({pending,busy:saving,title:t("Horarios y reservas")});

  const isEventsBusiness = useMemo(() => isEventLikeBusiness(businessDoc), [businessDoc]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!businessId || !db) return;
      try {
        const snap = await getDoc(doc(db, "businesses", businessId));
        if (!mounted || !snap.exists()) return;
        const data = snap.data() || {};
        setAvailability(normalizeAvailability(data.availability || data.openingHours || data.hours || data.schedule));
        setCapacity(Number(data.eventMaxSimultaneousBookings || data.maxSimultaneousEvents || 3));
        setBufferBefore(Number(data.eventBufferBeforeMinutes || data.bufferBeforeMinutes || 0));
        setBufferAfter(Number(data.eventBufferAfterMinutes || data.bufferAfterMinutes || 0));
        setNoticeHours(Number(data.minimumBookingNoticeHours || 0));
        setBlockedRanges(normalizeBlockedRanges(data));
        setSavedSignature(JSON.stringify([normalizeAvailability(data.availability || data.openingHours || data.hours || data.schedule),Number(data.eventMaxSimultaneousBookings || data.maxSimultaneousEvents || 3),Number(data.eventBufferBeforeMinutes || data.bufferBeforeMinutes || 0),Number(data.eventBufferAfterMinutes || data.bufferAfterMinutes || 0),Number(data.minimumBookingNoticeHours || 0),normalizeBlockedRanges(data)]));
        loadedRef.current = true;
      } catch (e) {
        console.error("Availability load failed", e);
        loadedRef.current = false;setStatus("No pudimos cargar los horarios. Recarga la sección antes de editar.");
      }
    }
    load();
    return () => {
      mounted = false;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [businessId, db]);

  const enabledDays = useMemo(() => DAYS.filter((d) => (availability[d.key] || []).length > 0).length, [availability]);

  const noticeLabel = useMemo(() => {
    const found = NOTICE_OPTIONS.find((op) => Number(op.value) === Number(noticeHours || 0));
    return t(found?.label || "Mismo momento");
  }, [noticeHours, language]);

  const rulesSummary = useMemo(() => {
    if (isEventsBusiness) {
      const totalBuffer = Number(bufferBefore || 0) + Number(bufferAfter || 0);
      return t("{count} eventos a la vez · {minutes} min entre reservas · {notice}", {count:capacity || 1,minutes:totalBuffer,notice:noticeLabel});
    }
    return noticeLabel;
  }, [isEventsBusiness, capacity, bufferBefore, bufferAfter, noticeLabel, language]);

  const { upcomingBlocked, historyBlocked } = useMemo(() => {
    const list = Array.isArray(blockedRanges) ? blockedRanges : [];
    const upcoming = [];
    const history = [];

    list.forEach((item) => {
      if (isPastBlockedRange(item)) history.push(item);
      else upcoming.push(item);
    });

    upcoming.sort((a, b) => String(a.from).localeCompare(String(b.from)));
    history.sort((a, b) => String(b.from).localeCompare(String(a.from)));

    return { upcomingBlocked: upcoming, historyBlocked: history };
  }, [blockedRanges]);

  const saveNow = async (options = {}) => {
    if (!businessId || !db || !loadedRef.current) return {ok:false,message:"Espera a que carguen los horarios antes de guardar."};
    if (saving) return {ok:false,message:"El guardado sigue en curso."};
    const validation=validateBusinessHours(availability,blockedRanges);
    if(validation){setStatus(validation);return {ok:false,message:validation};}
    const savingSignature=signature;
    setSaving(true);
    if (!options.silent) setStatus("");
    try {
      const normalizedBlockedRanges = (blockedRanges || [])
        .filter((r) => r?.from)
        .map((r, idx) => ({
          id: r.id || `blocked-${idx}-${r.from}`,
          from: r.from,
          to: r.to || r.from,
          reason: r.reason || "No disponible",
        }));

      await loopBusinessUpdateDoc(db, `businesses/${businessId}`, {
        availability,
        openingHours: availability,
        scheduleConfirmed: true,
        eventMaxSimultaneousBookings: Math.max(1, Number(capacity || 1)),
        maxSimultaneousEvents: Math.max(1, Number(capacity || 1)),
        eventBufferBeforeMinutes: Math.max(0, Number(bufferBefore || 0)),
        bufferBeforeMinutes: Math.max(0, Number(bufferBefore || 0)),
        eventBufferAfterMinutes: Math.max(0, Number(bufferAfter || 0)),
        bufferAfterMinutes: Math.max(0, Number(bufferAfter || 0)),
        minimumBookingNoticeHours: Math.max(0, Number(noticeHours || 0)),
        blockedRanges: normalizedBlockedRanges,
        blockedDates: normalizedBlockedRanges
          .filter((r) => r.from === r.to)
          .map((r) => r.from),
        updatedAt: loopBusinessNow(),
      });

      setSavedSignature(savingSignature);
      setStatus("Horarios guardados.");
      return {ok:true,message:"Horarios guardados."};
    } catch (e) {
      console.error("Availability save failed", e);
      setStatus("No pudimos guardar los horarios. Tus cambios siguen aquí.");
      return {ok:false,message:"No pudimos guardar los horarios. Intenta nuevamente."};
    } finally {
      setSaving(false);
    }
  };

  useEffect(()=>{if(!externalSaveRef)return;externalSaveRef.current=saveNow;return()=>{externalSaveRef.current=null;};});

  const toggleDay = (dayKey) => {
    setAvailability((prev) => {
      const current = prev[dayKey] || [];
      return {
        ...prev,
        [dayKey]: current.length ? [] : [{ from: "09:00", to: "18:00" }],
      };
    });
    setOpenDay(dayKey);
  };

  const updateRange = (dayKey, idx, patch) => {
    setAvailability((prev) => {
      const nextRanges = [...(prev[dayKey] || [])];
      nextRanges[idx] = { ...nextRanges[idx], ...patch };
      return { ...prev, [dayKey]: nextRanges };
    });
  };

  const addRange = (dayKey) => {
    setAvailability((prev) => ({
      ...prev,
      [dayKey]: [...(prev[dayKey] || []), { from: "09:00", to: "18:00" }],
    }));
    setOpenDay(dayKey);
  };

  const removeRange = (dayKey, idx) => {
    setAvailability((prev) => ({
      ...prev,
      [dayKey]: (prev[dayKey] || []).filter((_, i) => i !== idx),
    }));
  };

  const addBlockedRange = () => {
    if (!blockedFrom) {
      setStatus("Selecciona una fecha para bloquear.");
      return;
    }

    const from = blockedFrom;
    const to = blockedTo || blockedFrom;

    if (to < from) {
      setStatus("La fecha final no puede ser anterior a la inicial.");
      return;
    }

    const item = {
      id: `blocked-${Date.now()}`,
      from,
      to,
      reason: blockedReason.trim() || "No disponible",
    };

    setBlockedRanges((prev) => [...(prev || []), item]);
    setBlockedFrom("");
    setBlockedTo("");
    setBlockedReason("");
  };

  const removeBlockedRange = (id) => {
    setBlockedRanges((prev) => (prev || []).filter((x) => String(x.id) !== String(id)));
  };

  const formatRanges = (ranges = []) => {
    if (!Array.isArray(ranges) || !ranges.length) return t("Cerrado");
    return ranges.map((r) => `${r.from || "09:00"} – ${r.to || "18:00"}`).join(" · ");
  };

  const weekdaySummary = useMemo(() => {
    const monday = JSON.stringify(availability.monday || []);
    const sameWeekdays = ["tuesday", "wednesday", "thursday", "friday"].every((key) => JSON.stringify(availability[key] || []) === monday);
    const mondayRanges = availability.monday || [];
    const saturdayRanges = availability.saturday || [];
    const sundayRanges = availability.sunday || [];
    if (mondayRanges.length && sameWeekdays) {
      const satText = `${t("Sáb")} · ${formatRanges(saturdayRanges)}`;
      const sunText = `${t("Dom")} · ${formatRanges(sundayRanges)}`;
      return `${t("Lun")}–${t("Vie")} · ${formatRanges(mondayRanges)} · ${satText} · ${sunText}`;
    }
    if (!enabledDays) return t("Sin horario configurado");
    return t(enabledDays === 1 ? "{count} día activo" : "{count} días activos", {count:enabledDays});
  }, [availability, enabledDays, language]);

  const copyDayToWeekdays = (dayKey) => {
    const source = (availability[dayKey] || []).map((r) => ({ ...r }));
    setAvailability((prev) => ({ ...prev, monday: source, tuesday: source.map((r) => ({ ...r })), wednesday: source.map((r) => ({ ...r })), thursday: source.map((r) => ({ ...r })), friday: source.map((r) => ({ ...r })) }));
  };

  const copyDayToAllDays = (dayKey) => {
    const source = (availability[dayKey] || []).map((r) => ({ ...r }));
    setAvailability((prev) => DAYS.reduce((acc, day) => ({ ...acc, [day.key]: source.map((r) => ({ ...r })) }), { ...prev }));
  };

  const renderDayEditor = (day, mode = "inline") => {
    const ranges = availability[day.key] || [];
    const active = ranges.length > 0;
    return (
      <div className={`availability-day-editor ${mode === "sheet" ? "sheet" : ""}`}>
        <button type="button" className={`availability-open-toggle ${active ? "is-open" : ""}`} onClick={() => toggleDay(day.key)}>
          <span><strong>{active ? t("Abierto") : t("Cerrado")}</strong><em>{active ? t("Este día acepta reservas") : t("No acepta reservas este día")}</em></span>
          <i />
        </button>

        {active ? (
          <>
            <div className="availability-ranges-clean">
              {ranges.map((range, idx) => (
                <div className="availability-range-clean" key={`${day.key}-${idx}`}>
                  <label><span>{t("Desde")}</span><input type="time" value={range.from || "09:00"} onChange={(e) => updateRange(day.key, idx, { from: e.target.value })} /></label>
                  <label><span>Hasta</span><input type="time" value={range.to || "18:00"} onChange={(e) => updateRange(day.key, idx, { to: e.target.value })} /></label>
                  <button type="button" className="availability-delete-range" onClick={() => removeRange(day.key, idx)} aria-label={t("Eliminar rango")}>×</button>
                </div>
              ))}
            </div>
            <div className="availability-editor-actions">
              <button type="button" onClick={() => addRange(day.key)}>{t("+ Otra franja")}</button>
              <button type="button" onClick={() => copyDayToWeekdays(day.key)}>{t("Copiar Lun–Vie")}</button>
              <button type="button" onClick={() => copyDayToAllDays(day.key)}>{t("Copiar todos")}</button>
            </div>
          </>
        ) : (
          <p className="availability-closed-copy">{t("Este día aparecerá como cerrado para reservas online.")}</p>
        )}
      </div>
    );
  };

  const editingDayMeta = DAYS.find((d) => d.key === editingDay);
  const mobileDaySheet = editingDayMeta ? (
    <div className="availability-mobile-sheet-backdrop" onMouseDown={() => setEditingDay(null)}>
      <div className="availability-mobile-sheet" role="dialog" aria-modal="true" aria-label={`${t("Editar")} ${t(editingDayMeta.label)}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="availability-mobile-sheet-head">
          <div><span>{t("Horario diario")}</span><h4>{t(editingDayMeta.label)}</h4><p>{formatRanges(availability[editingDayMeta.key] || [])}</p></div>
          <button type="button" onClick={() => setEditingDay(null)} aria-label={t("Cerrar")}>×</button>
        </div>
        <div className="availability-mobile-sheet-body">{renderDayEditor(editingDayMeta, "sheet")}</div>
        <div className="availability-mobile-sheet-footer"><button type="button" onClick={() => setEditingDay(null)}>{t("Listo")}</button></div>
      </div>
    </div>
  ) : null;

  return (
    <section className="availability-shell availability-revamp-shell">
      <style>{styles}</style>

      <div className="availability-revamp-hero">
        <div className="availability-hero-copy">
          <span>{t(isEventsBusiness ? "Agenda de eventos" : "Agenda del negocio")}</span>
          <p>{weekdaySummary}</p>
        </div>
        <div className="availability-hero-metrics">
          <div><strong>{enabledDays}</strong><span>{t("días activos")}</span></div>
          <div><strong>{noticeLabel}</strong><span>{t("anticipación")}</span></div>
          <div><strong>{upcomingBlocked.length}</strong><span>{t("bloqueos activos")}</span></div>
        </div>
        <div className={`availability-save-pill ${saving ? "saving" : ""}`}>
          <i />
          {t(saving ? "Guardando..." : pending ? "Cambios pendientes de guardar" : status || "Revisa y guarda tus horarios")}
        </div>
      </div>

      {status && <p role="status" className="editor-inline-status">{t(status)}</p>}
      {!externalSaveRef && <button type="button" className="editor-primary" disabled={saving} onClick={()=>saveNow()}>{t("Guardar cambios")}</button>}
      <div className="availability-layout-grid">
        <div className="availability-left-column">
          <section className="availability-panel availability-week-panel">
            <div className="availability-panel-head">
              <div>
                <span>{t("Horario regular")}</span>
                <h4>{t("Horario semanal")}</h4>
                <p>{t("Activa los días donde aceptas reservas y configura una o varias franjas por día.")}</p>
              </div>
            </div>

            <div className="availability-week-list">
              {DAYS.map((day) => {
                const ranges = availability[day.key] || [];
                const active = ranges.length > 0;
                const expanded = openDay === day.key;
                return (
                  <article className={`availability-week-row ${active ? "is-active" : "is-closed"} ${expanded ? "is-expanded" : ""}`} key={day.key}>
                    <button type="button" className="availability-week-row-main" onClick={() => setOpenDay(expanded ? "" : day.key)}>
                      <span className="availability-day-dot">{t(day.short)}</span>
                      <span className="availability-week-copy"><strong>{t(day.label)}</strong><em>{formatRanges(ranges)}</em></span>
                      <span className={`availability-state-pill ${active ? "active" : ""}`}>{active ? t("Abierto") : t("Cerrado")}</span>
                      <span className="availability-web-expand">{expanded ? "Ocultar" : t("Editar")}</span>
                      <span className="availability-mobile-edit" onClick={(e) => { e.stopPropagation(); setEditingDay(day.key); }}>{t("Editar")}</span>
                    </button>
                    {expanded ? <div className="availability-desktop-editor">{renderDayEditor(day, "inline")}</div> : null}
                  </article>
                );
              })}
            </div>
          </section>
        </div>

        <aside className="availability-right-column">
          <section className={`availability-panel availability-rules-panel ${rulesOpen ? "is-open" : ""}`}>
            <button type="button" className="availability-rules-trigger" onClick={() => setRulesOpen((v) => !v)} aria-expanded={rulesOpen}>
              <div><span>{t("Reglas de reserva")}</span><h4>{rulesSummary}</h4><p>{t("Anticipación, capacidad y tiempos entre reservas.")}</p></div>
              <strong>{rulesOpen ? "Ocultar" : t("Editar")}</strong>
            </button>

            {rulesOpen ? (
              <div className="availability-rules-body">
                {isEventsBusiness ? (
                  <>
                    <ConfigCard badge="Capacidad" title={t("Eventos a la vez")} copy={t("Cantidad máxima en el mismo horario.")}><MiniStepper value={capacity} min={1} max={30} onChange={setCapacity} suffix="eventos" /></ConfigCard>
                    <ConfigCard badge="Antes" title={t("Tiempo antes")} copy={t("Preparación, traslado o montaje.")}><MiniStepper value={bufferBefore} min={0} max={240} onChange={setBufferBefore} suffix="min" /></ConfigCard>
                    <ConfigCard badge="Después" title={t("Tiempo después")} copy={t("Desmontaje o descanso.")}><MiniStepper value={bufferAfter} min={0} max={240} onChange={setBufferAfter} suffix="min" /></ConfigCard>
                  </>
                ) : null}
                <ConfigCard badge="Anticipación" title={t("Reservas")} copy={t("Cuánto tiempo antes pueden reservar.")}>
                  <select className="availability-pro-select" value={Number(noticeHours || 0)} onChange={(e) => setNoticeHours(Number(e.target.value || 0))}>
                    {NOTICE_OPTIONS.map((op) => <option key={op.value} value={op.value}>{t(op.label)}</option>)}
                  </select>
                </ConfigCard>
              </div>
            ) : null}
          </section>

          <section className="availability-panel availability-blocks-panel">
            <div className="availability-panel-head small">
              <div><span>{t("Excepciones")}</span><h4>{t("Días bloqueados")}</h4><p>{t("Vacaciones, eventos privados o días sin operación.")}</p></div>
              <button type="button" onClick={() => setBlockEditorOpen((v) => !v)}>{blockEditorOpen ? "Ocultar" : "+ Bloquear"}</button>
            </div>

            {blockEditorOpen ? (
              <div className="availability-block-builder-clean">
                <Field label={t("Desde")}><input type="date" value={blockedFrom} onChange={(e) => setBlockedFrom(e.target.value)} /></Field>
                <Field label="Hasta"><input type="date" value={blockedTo} onChange={(e) => setBlockedTo(e.target.value)} /></Field>
                <Field label={t("Motivo")}><input value={blockedReason} onChange={(e) => setBlockedReason(e.target.value)} placeholder={t("Vacaciones, evento privado...")} /></Field>
                <button type="button" className="availability-block-add" onClick={addBlockedRange}>{t("Bloquear fecha")}</button>
                <p>{t("Si es un solo día, deja") + " "}<strong>Hasta</strong>{" " + t("vacío.")}</p>
              </div>
            ) : null}

            <div className="availability-block-list-clean">
              {upcomingBlocked.length ? upcomingBlocked.map((item) => (
                <div className="availability-block-item-clean" key={item.id}>
                  <div><strong>{item.reason || t("No disponible")}</strong><span>{formatRangeNice(item)}</span></div>
                  <button type="button" onClick={() => removeBlockedRange(item.id)}>×</button>
                </div>
              )) : <div className="availability-empty-clean">{t("No hay fechas bloqueadas próximas.")}</div>}
            </div>

            <div className="availability-history-clean">
              <button type="button" onClick={() => setShowHistory((v) => !v)}>{t("Historial") + " "}{historyBlocked.length ? `(${historyBlocked.length})` : ""} <span>{showHistory ? "−" : "+"}</span></button>
              {showHistory ? (
                historyBlocked.length ? <div className="availability-block-list-clean history">{historyBlocked.map((item) => <div className="availability-block-item-clean muted" key={item.id}><div><strong>{item.reason || t("No disponible")}</strong><span>{formatRangeNice(item)}</span></div></div>)}</div> : <div className="availability-empty-clean small">{t("Todavía no hay historial.")}</div>
              ) : null}
            </div>
          </section>
        </aside>
      </div>

      {mobileDaySheet ? (typeof document !== "undefined" && document.body ? createPortal(mobileDaySheet, document.body) : mobileDaySheet) : null}
    </section>
  );
}

const styles = `
  .availability-revamp-shell{
    --av-brand:#00675f;
    --av-brand-dark:#003f3a;
    --av-line:#e5edf3;
    --av-muted:#64748b;
    --av-text:#0f172a;
    display:grid;
    gap:16px;
    width:100%;
    max-width:100%;
  }
  .availability-revamp-shell *{box-sizing:border-box;}
  .availability-revamp-hero{
    border:1px solid rgba(226,232,240,.95);
    border-radius:28px;
    background:radial-gradient(circle at 0% 0%,rgba(0,103,95,.10),transparent 34%),linear-gradient(135deg,#ffffff,#fbf7ef);
    box-shadow:0 18px 54px rgba(15,23,42,.06);
    padding:20px;
    display:grid;
    grid-template-columns:minmax(0,1fr) auto auto;
    gap:16px;
    align-items:center;
  }
  .availability-hero-copy span,.availability-panel-head span,.availability-rules-trigger span{
    display:block;
    color:var(--av-brand);
    font-size:10px;
    font-weight:950;
    letter-spacing:.15em;
    text-transform:uppercase;
    margin-bottom:6px;
  }
  .availability-hero-copy h3{
    margin:0;
    color:#123f39;
    font-size:34px;
    line-height:.96;
    font-weight:950;
    letter-spacing:-.07em;
  }
  .availability-hero-copy p{
    margin:7px 0 0;
    color:var(--av-muted);
    font-size:13px;
    font-weight:760;
    line-height:1.35;
    max-width:760px;
  }
  .availability-hero-metrics{display:flex;gap:8px;align-items:center;justify-content:flex-end;flex-wrap:wrap;}
  .availability-hero-metrics div{min-width:104px;border:1px solid rgba(226,232,240,.88);background:rgba(255,255,255,.82);border-radius:18px;padding:10px 12px;box-shadow:0 8px 22px rgba(15,23,42,.035);}
  .availability-hero-metrics strong{display:block;color:var(--av-brand);font-size:18px;font-weight:950;line-height:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:150px;}
  .availability-hero-metrics span{display:block;margin-top:4px;color:var(--av-muted);font-size:10px;font-weight:900;text-transform:uppercase;letter-spacing:.04em;}
  .availability-save-pill{display:inline-flex;align-items:center;gap:8px;min-height:36px;border-radius:999px;border:1px solid #ccfbf1;background:#f0fdfa;color:var(--av-brand);padding:0 12px;font-size:11px;font-weight:950;white-space:nowrap;justify-self:end;}
  .availability-save-pill i{width:8px;height:8px;border-radius:999px;background:#10b981;box-shadow:0 0 0 5px rgba(16,185,129,.11)}
  .availability-save-pill.saving i{animation:availabilityPulse 1s infinite}
  @keyframes availabilityPulse{50%{opacity:.35;transform:scale(.75)}}

  .availability-layout-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(310px,34%);gap:16px;align-items:start;}
  .availability-left-column,.availability-right-column{min-width:0;display:grid;gap:16px;}
  .availability-panel{border:1px solid rgba(226,232,240,.95);background:rgba(255,255,255,.96);border-radius:26px;box-shadow:0 14px 42px rgba(15,23,42,.05);overflow:hidden;}
  .availability-week-panel,.availability-blocks-panel{padding:18px;}
  .availability-panel-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:14px;}
  .availability-panel-head h4,.availability-rules-trigger h4{margin:0;color:var(--av-text);font-size:22px;line-height:1.04;font-weight:950;letter-spacing:-.055em;}
  .availability-panel-head p,.availability-rules-trigger p{margin:6px 0 0;color:var(--av-muted);font-size:12px;font-weight:740;line-height:1.35;}
  .availability-panel-head.small h4{font-size:19px;}
  .availability-panel-head button{height:34px;border:1px solid rgba(0,103,95,.16);background:#f0fdfa;color:var(--av-brand);border-radius:999px;padding:0 12px;font-size:11px;font-weight:950;white-space:nowrap;cursor:pointer;}

  .availability-week-list{display:grid;gap:8px;}
  .availability-week-row{border:1px solid #e5edf3;background:#f8fafc;border-radius:18px;overflow:hidden;transition:.16s ease;}
  .availability-week-row.is-active{background:#fff;}
  .availability-week-row.is-expanded{border-color:rgba(0,103,95,.25);box-shadow:0 12px 28px rgba(15,23,42,.045);}
  .availability-week-row-main{width:100%;border:0;background:transparent;display:grid;grid-template-columns:44px minmax(0,1fr) auto auto;gap:10px;align-items:center;text-align:left;padding:10px 12px;cursor:pointer;}
  .availability-day-dot{width:36px;height:36px;border-radius:14px;background:#fff;border:1px solid #e5edf3;color:var(--av-brand);display:grid;place-items:center;font-size:11px;font-weight:950;}
  .availability-week-copy{min-width:0;}
  .availability-week-copy strong{display:block;color:var(--av-text);font-size:14px;font-weight:950;}
  .availability-week-copy em{display:block;margin-top:3px;color:var(--av-muted);font-style:normal;font-size:12px;font-weight:780;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .availability-state-pill{height:26px;border:1px solid #e5edf3;background:#fff;color:#64748b;border-radius:999px;display:inline-flex;align-items:center;padding:0 9px;font-size:10px;font-weight:950;white-space:nowrap;}
  .availability-state-pill.active{border-color:#99f6e4;background:#ecfdf5;color:var(--av-brand);}
  .availability-web-expand{height:28px;display:inline-flex;align-items:center;justify-content:center;border-radius:999px;background:rgba(15,23,42,.04);color:#475569;padding:0 10px;font-size:11px;font-weight:950;white-space:nowrap;}
  .availability-mobile-edit{display:none;}
  .availability-desktop-editor{border-top:1px solid #edf2f7;background:#fff;padding:12px;}

  .availability-day-editor{display:grid;gap:12px;}
  .availability-open-toggle{width:100%;border:1px solid #e5edf3;background:#fff;border-radius:18px;padding:10px 12px;display:flex;align-items:center;justify-content:space-between;gap:12px;text-align:left;cursor:pointer;}
  .availability-open-toggle strong{display:block;color:var(--av-text);font-size:13px;font-weight:950;}
  .availability-open-toggle em{display:block;margin-top:2px;color:var(--av-muted);font-size:11px;font-style:normal;font-weight:760;}
  .availability-open-toggle i{width:42px;height:24px;border-radius:999px;background:#e2e8f0;position:relative;flex:0 0 auto;}
  .availability-open-toggle i:after{content:"";position:absolute;width:18px;height:18px;border-radius:999px;background:#fff;left:3px;top:3px;box-shadow:0 2px 6px rgba(15,23,42,.18);transition:.16s ease;}
  .availability-open-toggle.is-open{background:#f0fdfa;border-color:#99f6e4;}
  .availability-open-toggle.is-open i{background:var(--av-brand);}
  .availability-open-toggle.is-open i:after{left:21px;}
  .availability-ranges-clean{display:grid;gap:8px;}
  .availability-range-clean{display:grid;grid-template-columns:1fr 1fr 34px;gap:8px;align-items:end;border:1px solid #edf2f7;background:#fbfdff;border-radius:18px;padding:10px;}
  .availability-range-clean label,.availability-field{display:grid;gap:6px;}
  .availability-range-clean label span,.availability-field span{color:#475569;font-size:11px;font-weight:950;}
  .availability-range-clean input,.availability-field input{width:100%;min-height:38px;border:1px solid #e2e8f0;border-radius:13px;background:#fff;color:var(--av-text);font-size:12px;font-weight:850;padding:0 10px;outline:none;}
  .availability-delete-range{width:34px;height:38px;border:0;border-radius:12px;background:#fef2f2;color:#dc2626;font-size:16px;font-weight:950;cursor:pointer;}
  .availability-editor-actions{display:flex;gap:8px;flex-wrap:wrap;}
  .availability-editor-actions button{height:34px;border:1px solid rgba(0,103,95,.16);background:#f0fdfa;color:var(--av-brand);border-radius:999px;padding:0 12px;font-size:11px;font-weight:950;cursor:pointer;}
  .availability-closed-copy{margin:0;color:#64748b;font-size:12px;font-weight:750;line-height:1.4;}

  .availability-rules-panel{padding:0;}
  .availability-rules-trigger{width:100%;border:0;background:radial-gradient(circle at 0% 0%,rgba(0,103,95,.075),transparent 34%),linear-gradient(135deg,#ffffff,#fbfefd);display:flex;align-items:center;justify-content:space-between;gap:14px;text-align:left;padding:16px;cursor:pointer;}
  .availability-rules-trigger strong{height:32px;display:inline-flex;align-items:center;border:1px solid rgba(0,103,95,.16);background:#f0fdfa;color:var(--av-brand);border-radius:999px;padding:0 12px;font-size:11px;font-weight:950;white-space:nowrap;}
  .availability-rules-panel.is-open .availability-rules-trigger{border-bottom:1px solid #edf2f7;}
  .availability-rules-body{display:grid;gap:10px;padding:12px;}
  .availability-config-card{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;align-items:center;border:1px solid #edf2f7;background:#fff;border-radius:18px;padding:12px;}
  .availability-config-copy span{display:block;color:var(--av-brand);font-size:9px;font-weight:950;letter-spacing:.10em;text-transform:uppercase;margin-bottom:4px;}
  .availability-config-copy h5{margin:0;color:var(--av-text);font-size:13px;font-weight:950;letter-spacing:-.02em;}
  .availability-config-copy p{margin:4px 0 0;color:#94a3b8;font-size:11px;font-weight:720;line-height:1.28;}
  .availability-config-control{display:flex;justify-content:flex-end;}
  .availability-stepper{width:146px;border:1px solid #e2e8f0;background:#fff;border-radius:16px;display:grid;grid-template-columns:36px 1fr 36px;align-items:center;overflow:hidden;}
  .availability-stepper button{height:40px;border:0;background:#f8fafc;color:var(--av-text);font-size:16px;font-weight:950;cursor:pointer;}
  .availability-stepper button:disabled{opacity:.35;}
  .availability-stepper div{text-align:center;}
  .availability-stepper strong{display:block;color:var(--av-text);font-size:17px;line-height:1;font-weight:950;}
  .availability-stepper span{display:block;margin-top:2px;color:#64748b;font-size:9px;font-weight:900;}
  .availability-pro-select{width:150px;min-height:40px;border:1px solid #e2e8f0;border-radius:15px;background:#fff;color:var(--av-text);padding:0 10px;font-size:12px;font-weight:900;outline:none;}

  .availability-block-builder-clean{border:1px solid #edf2f7;background:#f8fafc;border-radius:18px;padding:12px;display:grid;gap:10px;margin-bottom:12px;}
  .availability-block-builder-clean p{margin:0;color:#64748b;font-size:11px;font-weight:740;line-height:1.35;}
  .availability-block-builder-clean p strong{color:var(--av-text);font-weight:950;}
  .availability-block-add{min-height:38px;border:0;border-radius:13px;background:linear-gradient(135deg,#005f57,#007467);color:#fff;font-size:12px;font-weight:950;padding:0 13px;box-shadow:0 10px 22px rgba(0,103,95,.14);cursor:pointer;}
  .availability-block-list-clean{display:grid;gap:8px;}
  .availability-block-item-clean{display:flex;align-items:center;justify-content:space-between;gap:10px;border:1px solid #ccfbf1;background:#f0fdfa;border-radius:16px;padding:10px 10px 10px 12px;}
  .availability-block-item-clean strong{display:block;color:var(--av-text);font-size:12px;font-weight:950;}
  .availability-block-item-clean span{display:block;margin-top:2px;color:#64748b;font-size:11px;font-weight:800;}
  .availability-block-item-clean button{width:28px;height:28px;border:0;border-radius:999px;background:#fff;color:#64748b;font-size:15px;font-weight:950;cursor:pointer;}
  .availability-block-item-clean.muted{background:#f8fafc;border-color:#e2e8f0;opacity:.86;}
  .availability-empty-clean{border:1px dashed #cbd5e1;background:#fff;color:#64748b;border-radius:16px;padding:12px;font-size:12px;font-weight:850;}
  .availability-empty-clean.small{margin-top:8px;}
  .availability-history-clean{margin-top:12px;border-top:1px solid #edf2f7;padding-top:8px;}
  .availability-history-clean>button{width:100%;height:34px;border:0;background:#fff;color:var(--av-text);font-size:12px;font-weight:950;display:flex;justify-content:space-between;align-items:center;border-radius:12px;cursor:pointer;}
  .availability-history-clean>button span{color:#94a3b8;font-size:16px;}
  .availability-field em{color:#94a3b8;font-size:11px;font-style:normal;font-weight:750;line-height:1.25;}

  .availability-mobile-sheet-backdrop{position:fixed;inset:0;z-index:60000;background:rgba(15,23,42,.46);display:grid;align-items:end;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);}
  .availability-mobile-sheet{background:#fff;border-radius:26px 26px 0 0;box-shadow:0 -24px 70px rgba(15,23,42,.32);max-height:86dvh;overflow:hidden;display:grid;grid-template-rows:auto minmax(0,1fr) auto;}
  .availability-mobile-sheet-head{padding:18px 18px 14px;border-bottom:1px solid #edf2f7;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
  .availability-mobile-sheet-head span{display:block;color:var(--av-brand);font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.14em;margin-bottom:5px;}
  .availability-mobile-sheet-head h4{margin:0;color:var(--av-text);font-size:24px;font-weight:950;letter-spacing:-.06em;}
  .availability-mobile-sheet-head p{margin:5px 0 0;color:#64748b;font-size:12px;font-weight:760;}
  .availability-mobile-sheet-head>button{width:38px;height:38px;border:0;border-radius:999px;background:#f8fafc;color:var(--av-text);font-size:24px;line-height:1;cursor:pointer;}
  .availability-mobile-sheet-body{padding:14px 16px;overflow:auto;}
  .availability-mobile-sheet-footer{padding:12px 16px calc(14px + env(safe-area-inset-bottom,0px));border-top:1px solid #edf2f7;background:#fff;}
  .availability-mobile-sheet-footer button{width:100%;height:46px;border:0;border-radius:16px;background:linear-gradient(135deg,#005f57,#007467);color:#fff;font-size:14px;font-weight:950;}

  @media(max-width:1180px){
    .availability-revamp-hero{grid-template-columns:1fr;}
    .availability-save-pill{justify-self:start;}
    .availability-layout-grid{grid-template-columns:1fr;}
    .availability-right-column{grid-template-columns:1fr 1fr;}
  }
  @media(max-width:820px){
    .availability-revamp-shell{gap:12px;}
    .availability-revamp-hero{border-radius:24px;padding:16px;gap:12px;}
    .availability-hero-copy h3{font-size:30px;}
    .availability-hero-copy p{font-size:12px;}
    .availability-hero-metrics{display:grid;grid-template-columns:1fr 1fr 1fr;width:100%;gap:7px;}
    .availability-hero-metrics div{min-width:0;border-radius:15px;padding:9px 10px;}
    .availability-hero-metrics strong{font-size:14px;max-width:100%;}
    .availability-hero-metrics span{font-size:9px;}
    .availability-right-column{grid-template-columns:1fr;}
    .availability-week-panel,.availability-blocks-panel{padding:14px;border-radius:22px;}
    .availability-panel-head h4,.availability-rules-trigger h4{font-size:20px;}
    .availability-panel-head p,.availability-rules-trigger p{font-size:11px;}
    .availability-week-row-main{grid-template-columns:38px minmax(0,1fr) auto;gap:9px;padding:9px 10px;}
    .availability-day-dot{width:32px;height:32px;border-radius:12px;}
    .availability-state-pill,.availability-web-expand{display:none;}
    .availability-mobile-edit{height:28px;display:inline-flex;align-items:center;justify-content:center;border-radius:999px;background:#f0fdfa;color:var(--av-brand);padding:0 10px;font-size:11px;font-weight:950;}
    .availability-desktop-editor{display:none;}
    .availability-week-row.is-expanded{box-shadow:none;border-color:#e5edf3;}
    .availability-rules-trigger{display:grid;padding:14px;}
    .availability-rules-trigger strong{justify-self:start;}
    .availability-config-card{grid-template-columns:1fr;}
    .availability-config-control{justify-content:flex-start;}
    .availability-stepper,.availability-pro-select{width:100%;}
    .availability-range-clean{grid-template-columns:1fr 1fr 34px;}
  }
  @media(max-width:460px){
    .availability-hero-metrics{grid-template-columns:1fr;}
    .availability-save-pill{width:100%;justify-content:center;}
    .availability-range-clean{grid-template-columns:1fr;}
    .availability-delete-range{width:100%;}
    .availability-editor-actions{display:grid;grid-template-columns:1fr;}
  }


/* =========================================================
   LOOP AVAILABILITY — BUSINESS EDIT INTEGRATION FIX
   - BusinessEditPage already owns the big page title.
   - This section starts compactly without a duplicate "Disponibilidad" H3.
   - Default schedule is fully closed until the user actively opens days.
   ========================================================= */
.availability-revamp-hero{
  padding:16px 18px!important;
  border-radius:24px!important;
  grid-template-columns:minmax(0,1fr) auto auto!important;
  gap:14px!important;
}

.availability-hero-copy span{
  margin-bottom:4px!important;
}

.availability-hero-copy p{
  margin:0!important;
  font-size:13px!important;
  max-width:840px!important;
}

.availability-hero-metrics div{
  min-width:96px!important;
  padding:9px 11px!important;
  border-radius:16px!important;
}

.availability-save-pill{
  min-height:34px!important;
}

.availability-week-row.is-expanded.is-closed{
  border-color:rgba(0,103,95,.18)!important;
  background:#fff!important;
}

.availability-week-row.is-expanded.is-closed .availability-desktop-editor{
  background:#fff!important;
}

.availability-closed-copy{
  border:1px dashed #cbd5e1!important;
  border-radius:14px!important;
  background:#f8fafc!important;
  padding:11px 12px!important;
}

@media(max-width:1180px){
  .availability-revamp-hero{
    grid-template-columns:1fr!important;
  }
}

@media(max-width:820px){
  .availability-revamp-hero{
    padding:14px!important;
    border-radius:22px!important;
  }

  .availability-hero-metrics{
    margin-top:2px!important;
  }
}



/* =========================================================
   LOOP AVAILABILITY — RESERVATION RULES READABILITY FIX
   Makes the booking-rules summary and notice dropdown readable and premium.
   ========================================================= */

/* Top hero metrics should not truncate important labels like "Mismo momento" */
.availability-hero-metrics{
  gap:10px!important;
}

.availability-hero-metrics div{
  min-width:128px!important;
  max-width:190px!important;
  padding:11px 13px!important;
}

.availability-hero-metrics strong{
  max-width:none!important;
  overflow:visible!important;
  text-overflow:clip!important;
  white-space:normal!important;
  line-height:1.08!important;
  font-size:17px!important;
}

.availability-hero-metrics span{
  line-height:1.15!important;
}

/* Rules card: give content room and avoid cropped copy */
.availability-rules-trigger{
  padding:18px!important;
  align-items:flex-start!important;
}

.availability-rules-trigger > div{
  min-width:0!important;
  max-width:100%!important;
}

.availability-rules-trigger h4{
  font-size:22px!important;
  line-height:1.02!important;
  overflow:visible!important;
  text-overflow:clip!important;
  white-space:normal!important;
}

.availability-rules-trigger p{
  font-size:12.5px!important;
  line-height:1.35!important;
  max-width:100%!important;
}

.availability-rules-trigger strong{
  flex:0 0 auto!important;
}

/* Rule body: cleaner rows */
.availability-rules-body{
  padding:14px!important;
  gap:12px!important;
}

.availability-config-card{
  grid-template-columns:minmax(0,1fr) minmax(180px,220px)!important;
  padding:14px!important;
  gap:14px!important;
  align-items:center!important;
}

.availability-config-copy h5{
  font-size:14px!important;
}

.availability-config-copy p{
  font-size:11.5px!important;
}

/* Premium select: wider, readable, no clipped selected value */
.availability-pro-select{
  width:100%!important;
  min-width:190px!important;
  height:46px!important;
  min-height:46px!important;
  border-radius:15px!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:
    linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)!important;
  color:#0f172a!important;
  padding:0 42px 0 14px!important;
  font-size:14px!important;
  font-weight:900!important;
  line-height:1.15!important;
  outline:none!important;
  box-shadow:0 8px 20px rgba(15,23,42,.035)!important;
  cursor:pointer!important;
  text-overflow:clip!important;
  white-space:normal!important;
}

.availability-pro-select:hover{
  border-color:rgba(0,103,95,.28)!important;
}

.availability-pro-select:focus{
  border-color:rgba(0,103,95,.46)!important;
  box-shadow:0 0 0 4px rgba(0,103,95,.09), 0 8px 20px rgba(15,23,42,.04)!important;
}

.availability-pro-select option{
  font-size:14px!important;
  font-weight:800!important;
  color:#0f172a!important;
  background:#fff!important;
  padding:10px 12px!important;
}

/* Event stepper alignment stays consistent with select width */
.availability-stepper{
  width:100%!important;
  min-width:190px!important;
  max-width:220px!important;
  min-height:46px!important;
}

.availability-stepper button{
  height:46px!important;
}

@media(max-width:1180px){
  .availability-hero-metrics{
    justify-content:start!important;
  }

  .availability-hero-metrics div{
    min-width:118px!important;
  }
}

@media(max-width:820px){
  .availability-hero-metrics{
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
  }

  .availability-hero-metrics div{
    min-width:0!important;
    max-width:none!important;
  }

  .availability-hero-metrics strong{
    font-size:14px!important;
  }

  .availability-config-card{
    grid-template-columns:1fr!important;
    align-items:stretch!important;
  }

  .availability-config-control{
    justify-content:stretch!important;
  }

  .availability-pro-select,
  .availability-stepper{
    min-width:0!important;
    width:100%!important;
    max-width:none!important;
  }
}

@media(max-width:460px){
  .availability-hero-metrics{
    grid-template-columns:1fr!important;
  }

  .availability-hero-metrics strong{
    font-size:16px!important;
  }
}

`;
